import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Components
import SearchSelectInputV2 from '../../../../../../common/src/components/SearchSelectInputV2';

// Types
import { ReturnOptionProps } from '../../../../../../../types/ProductFactory';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { FormOption } from '../../../../../../../types/Form';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useQuestionEnum from '../../../../hooks/useQuestionEnum';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { handleResponseLanguage } from '../../../../../../../i18n';
import { useDebouncedCallback } from 'use-debounce';

// Local interface for enum_source response structure
interface EnumSourceResponse {
  items: Array<{
    value: string;
    icon_key?: string | null;
    label: {
      nl?: string | null;
      en?: string | null;
      fr?: string | null;
      de?: string | null;
      es?: string | null;
    };
  }>;
}

interface Props {
  question: FlowsQuestion;
  isSmallDesktopDisplay?: boolean;
}

const SelectInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  isSmallDesktopDisplay,
}) => {
  const {
    field: { onBlur, value },
    prefilled,
    error,
  } = useFlowInputComponent({ question });

  const { t } = useTranslation();

  const handleOnChange = ({ name, value }: ReturnOptionProps) => {
    onBlur({ name, value: value as string });
  };

  const enumOptions = useQuestionEnum(question);

  const hasEnumSource = Boolean(question.rules.enum_source);

  // Directly fetch enum options from enum_source when user types
  const fetchEnumOptions = React.useCallback(
    async (value: string, isPrefilledSearch = false): Promise<FormOption[]> => {
      if (!hasEnumSource || (!isPrefilledSearch && value.length < 3)) return [];

      try {
        const { data, isError } = await wgApi.get<EnumSourceResponse>(
          `${question.rules.enum_source?.url}?q=${encodeURIComponent(value)}`,
        );

        if (isError) return [];

        return (data.items || []).map(
          (item: EnumSourceResponse['items'][0]): FormOption => ({
            label: handleResponseLanguage(item.label) || item.value,
            value: item.value,
            icon_key: item.icon_key || undefined,
          }),
        );
      } catch {
        return [];
      }
    },
    [hasEnumSource, question.rules.enum_source?.url],
  );

  // Debounce the fetch function
  const [debouncedFetch] = useDebouncedCallback(
    async (v: string, cb: (o: FormOption[]) => void) => {
      const opts = await fetchEnumOptions(v);
      setEnumSourceOptions(opts);
      cb(opts);
    },
    300,
  );

  // State for enum_source options
  const [enumSourceOptions, setEnumSourceOptions] = React.useState<
    FormOption[]
  >([]);

  const options = hasEnumSource ? enumSourceOptions : enumOptions;

  const loadOptions = (
    inputValue: string,
    callback: (opts: unknown) => void,
  ) => {
    debouncedFetch(inputValue, callback as (o: FormOption[]) => void);
  };

  // Auto-search for prefilled value when using enum_source
  React.useEffect(() => {
    if (hasEnumSource && value && enumSourceOptions.length === 0) {
      fetchEnumOptions(value, true).then((options) => {
        setEnumSourceOptions(options);
      });
    }
  }, [hasEnumSource, value, fetchEnumOptions, enumSourceOptions.length]);

  const getPlaceholder = () => {
    if (hasEnumSource) {
      return t('FLOWS_PRODUCT_TYPE_TO_SEARCH_MIN_3_CHARS');
    }
    return t('Choose an option');
  };

  return (
    <SearchSelect
      placeholder={getPlaceholder()}
      name={question.id}
      onSelected={handleOnChange}
      options={hasEnumSource ? undefined : options}
      error={error?.message}
      value={options.find(({ value: _value }) => value === _value)}
      prefilled={prefilled}
      dataTestId={`FLOWS_SELECT-INPUT_${question.id
        .toUpperCase()
        .replaceAll('.', '-')}`}
      menuPlacement={isSmallDesktopDisplay ? 'top' : 'auto'}
      sort={false}
      disabled={question.rules.is_disabled}
      async={hasEnumSource}
      loadOptions={hasEnumSource ? loadOptions : undefined}
      loading={false}
      cacheOptions={false}
      isClearable={hasEnumSource}
    />
  );
};

const SearchSelect = styled(SearchSelectInputV2)`
  width: 100%;
  height: unset;

  label > div {
    border-radius: 6px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    margin-bottom: 0;
    margin-top: 0;
  }
`;

export default SelectInputComponent;
